/* -------------------------------------------------------------------------------------
Vuex module to manage user interation (e.g. login, register)

Main components
---------------
mutations:
- SET_USER_DATA: write user data into vuex state and local storage
- CLEAR_USER_DATA: remove user data from vuex state and local storage

actions:
- getUserSpecificData: fetch user specific data from API and save in store
- getUnspecificData: request unspecific data from API and save in store
- login: call API functioin to register new user in DB
- logout: clear user data from vuex state an local storage
- register: call API function to register new user in DB
- setUserData: setUserData is called when user data is available in local storage (this is the auto-login function)
- updateEmail: Update the email address of the user
- updatePassword: Update password of the user
- updateUser: Update user data

getters:
- loggedIn: returns true if user is logged in

TASKS:
- Can action getUserSpecificData from login, register be called instead of individual calls?
---------------------------------------------------------------------------------------*/

// import tourdb library
import ApiService from '@/services/ApiService'

export const namespaced = true

export const state = {
  user: null, // Object for user data
}

export const mutations = {

  // write user data into vuex state and local storage
  SET_USER_DATA(state, userData) {
    state.user = userData
    localStorage.setItem('user', JSON.stringify(userData))
  },

  // remove user data from vuex state and local storage
  CLEAR_USER_DATA() {
    localStorage.removeItem('user')
    location.reload() // forces page to reload and as user data is removed from the local storage, he is not logged in anymore
  }
}

export const actions = {

  // fetch user specific data from API and save in store
  getUserSpecificData({ dispatch }) {
    dispatch('tracksKml/getTracksKmlInitial', {
      trackIds: [], // send empty array (indicating that this is an initial load)
      format: 'json', // send expected return format 
      limit: 30 // number of tracks to be loaded initially on map
      }, { root: true }) // fetch initial setup of KML tracks
    dispatch('participant/fetchParticipants', '', { root: true }) // fetch participants
    dispatch('track/fetchTracks', '', { root: true }) // fetch tracks
    dispatch('waypoint/fetchWaypointsPerTrack', '', { root: true }) // fetch list of waypoints per track
    dispatch('waypoint/fetchWaypointsList', '', { root: true }) // fetch list of waypoints per track
    dispatch('participant/fetchParticipantsStats', '', { root: true }) // fetch list of waypoints per track
    dispatch('tag/fetchTags', '', { root: true }) // fetch list of tags
  },

  // request unspecific data from API and save in store
  getUnspecificData({ dispatch }) {
    dispatch('area/fetchAreas', '', { root: true }) // fetch areas
    dispatch('grade/fetchGrades', '', { root: true }) // fetch grades (all types) 
    dispatch('type/fetchTypes', '', { root: true }) // fetch types (types and subtypes)
    dispatch('waypoint/fetchWaypoints', '', { root: true }) // fetch waypoints (all types)
  },

  // call API function to register new user in DB
  login({ commit, dispatch }, credentials) {
    ApiService.userLogin(credentials).then(({ data }) => {
      commit('SET_USER_DATA', data)

      // fetch user specific data from API --> ACTION: Can action getUserSpecificData be called in stead?
      dispatch('tracksKml/getTracksKmlInitial', {
        trackIds: [], // send empty array (indicating that this is an initial load)
        format: 'json', // send expected return format 
        limit: 30 // number of tracks to be loaded initially on map
        }, { root: true }), // fetch initial setup of KML tracks
      dispatch('participant/fetchParticipants', '', { root: true }) // fetch participants
      dispatch('track/fetchTracks', '', { root: true }) // fetch tracks

      // request unspecific data from API --> ACTION: Can action getUserUnsSpecificData be called in stead?
      dispatch('area/fetchAreas', '', { root: true }) // fetch areas
      dispatch('grade/fetchGrades', '', { root: true }) // fetch grades (all types)
      dispatch('type/fetchTypes', '', { root: true }) // fetch types (types and subtypes)
      dispatch('waypoint/fetchWaypoints', '', { root: true }) // fetch waypoints (all types)

      // Set notification
      const notification = {
        type: 'success',
        message: 'Login successful'
      }
      dispatch('notification/add', notification, { root: true })
    })
  },

  // logout: clear user data from vuex state an local storage
  logout({ commit, dispatch }) {
    commit('CLEAR_USER_DATA')
    const notification = {
      type: 'success',
      message: 'User logged out'
    }
    dispatch('notification/add', notification, { root: true })
  },

  // call API function to register new user in DB
  register({ commit, dispatch }, credentials) {
    ApiService.userRegister(credentials).then(({ data }) => {
      commit('SET_USER_DATA', data)

      // fetch user specific data from API
      dispatch('tracksKml/getTracksKmlInitial', {
        trackIds: [], // send empty array (indicating that this is an initial load)
        format: 'json', // send expected return format 
        limit: 30 // number of tracks to be loaded initially on map
        }, { root: true }) // fetch initial setup of KML tracks
      dispatch('participant/fetchParticipants', '', { root: true }) // fetch participants
      dispatch('track/fetchTracks', '', { root: true }) // fetch tracks

      // request unspecific data from API
      dispatch('area/fetchAreas', '', { root: true }) // fetch areas
      dispatch('grade/fetchGrades', '', { root: true }) // fetch grades (all types)
      dispatch('type/fetchTypes', '', { root: true }) // fetch types (types and subtypes)
      dispatch('waypoint/fetchWaypoints', '', { root: true }) // fetch waypoints (all types)

      // set notification
      const notification = {
        type: 'success',
        message: 'User successfully created'
      }
      dispatch('notification/add', notification, { root: true })
    })
  },

  // setUserData is called when user data is available in local storage (this is the auto-login function)
  // Once the user data is set, the user specific data and then the unspecific data is fetched from the API
  setUserData({ commit }, userData) {
    commit('SET_USER_DATA', userData) // set user data in vuex
  },

  // Update the email address of the user
  updateEmail({ dispatch }, userProfile) {
    ApiService.updateEmail(userProfile.id, userProfile.email)
      .then(() => {
        const notification = {
          type: 'success',
          message: 'The password was updated successfully'
        }
        dispatch('notification/add', notification, { root: true })
        dispatch('user/setUserData', userProfile, { root: true })
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message: 'There was an error updating the password: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // Update password of the user
  updatePassword({ dispatch }, { userProfile }) {
    ApiService.updatePassword(userProfile.id, userProfile.password)
      .then(() => {
        const notification = {
          type: 'success',
          message: 'The password was updated successfully'
        }
        dispatch('notification/add', notification, { root: true })
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message: 'There was an error updating the password: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // Update user data
  updateUser({ dispatch }, userProfile) {
    ApiService.updateUser(userProfile.id, userProfile)
      .then(() => {
        const notification = {
          type: 'success',
          message: 'The user was updated successfully'
        }
        dispatch('notification/add', notification, { root: true })
        dispatch('user/setUserData', userProfile, { root: true })
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message: 'There was an error updating the user: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  }
}

export const getters = {

  // return true when user is logged in, false if not
  loggedIn(state) {
    return !!state.user 
  }
}
